import { useCallback, useEffect, useRef, useState } from 'react'
import { logger } from '../Helpers'

export interface PaginatedFetchResult<T> {
  data: T[]
  total: number
}

export interface UseApiPaginatedOptionsParams<T> {
  fetchPage: (
    page: number,
    pageSize: number,
    searchText: string,
    signal: AbortSignal,
  ) => Promise<PaginatedFetchResult<T>>
  getOptionId: (option: T) => string | number
  pageSize?: number
  searchText?: string
  enabled?: boolean
  logContext?: string
}

export interface UseApiPaginatedOptionsResult<T> {
  options: T[]
  isLoading: boolean
  isFetchingMore: boolean
  hasMore: boolean
  error: string | null
  loadMore: () => void
  retry: () => void
  reset: () => void
}

const DEFAULT_PAGE_SIZE = 20

/**
 * Generic API-driven paginated option loader for dropdowns: lazy loads on
 * demand, de-dupes by id, resets on search change, and ignores stale/aborted
 * responses. The API only returns { data, total } (no hasMore flag), so
 * `hasMore` is derived here from how many records we've loaded vs the total.
 */
function useApiPaginatedOptions<T>({
  fetchPage,
  getOptionId,
  pageSize = DEFAULT_PAGE_SIZE,
  searchText = '',
  enabled = true,
  logContext = 'paginated-options',
}: UseApiPaginatedOptionsParams<T>): UseApiPaginatedOptionsResult<T> {
  const [options, setOptions] = useState<T[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pageRef = useRef(0)
  const isFetchingRef = useRef(false)
  const sessionRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)
  const seenIdsRef = useRef<Set<string | number>>(new Set())

  const hasMore = options.length < total

  const fetchPageInternal = useCallback(
    async (pageNumber: number, session: number) => {
      if (isFetchingRef.current) return
      isFetchingRef.current = true

      const controller = new AbortController()
      abortRef.current = controller
      pageNumber === 1 ? setIsLoading(true) : setIsFetchingMore(true)
      setError(null)

      try {
        const result = await fetchPage(pageNumber, pageSize, searchText, controller.signal)
        if (session !== sessionRef.current) return // stale (search changed / closed / unmounted)

        setTotal(result.total)
        setOptions((prev) => {
          const merged = pageNumber === 1 ? [] : [...prev]
          result.data.forEach((item) => {
            const id = getOptionId(item)
            if (!seenIdsRef.current.has(id)) {
              seenIdsRef.current.add(id)
              merged.push(item)
            }
          })
          return merged
        })
        pageRef.current = pageNumber
      } catch (err: any) {
        if (controller.signal.aborted || session !== sessionRef.current) return
        setError(err?.message ?? 'Failed to fetch options')
        logger.error({ context: logContext, page: pageNumber, searchText, error: err?.message })
      } finally {
        if (session === sessionRef.current) {
          setIsLoading(false)
          setIsFetchingMore(false)
        }
        isFetchingRef.current = false
      }
    },
    [fetchPage, pageSize, searchText, getOptionId, logContext],
  )

  const reset = useCallback(() => {
    abortRef.current?.abort()
    sessionRef.current += 1
    isFetchingRef.current = false
    seenIdsRef.current = new Set()
    pageRef.current = 0
    setOptions([])
    setTotal(0)
    setError(null)
    fetchPageInternal(1, sessionRef.current)
  }, [fetchPageInternal])

  useEffect(() => {
    if (enabled) reset()
  }, [enabled, searchText])

  useEffect(
    () => () => {
      sessionRef.current += 1
      abortRef.current?.abort()
    },
    [],
  )

  const loadMore = useCallback(() => {
    if (!enabled || isFetchingRef.current || !hasMore || isLoading || error) return
    fetchPageInternal(pageRef.current + 1, sessionRef.current)
  }, [enabled, hasMore, isLoading, error, fetchPageInternal])

  const retry = useCallback(() => {
    if (options.length === 0) {
      reset()
      return
    }
    abortRef.current?.abort()
    isFetchingRef.current = false
    setError(null)
    fetchPageInternal(pageRef.current + 1, sessionRef.current)
  }, [options.length, reset, fetchPageInternal])

  return { options, isLoading, isFetchingMore, hasMore, error, loadMore, retry, reset }
}

export default useApiPaginatedOptions
